import React from 'react'
import { useNotifications as useNotificationContext } from '@/context/NotificationContext'
import { NotificationListParams } from '@/pages/NotificationCenter/types'

/**
 * Hook for accessing notification data and basic operations
 *
 * This is a convenience wrapper around NotificationContext that provides
 * a clean interface for components without exposing internal context details.
 *
 * @example
 * ```typescript
 * function NotificationList() {
 *   const {
 *     notifications,
 *     loading,
 *     loadingMore,
 *     error,
 *     hasMore,
 *     realtime,
 *     fetchNotifications,
 *     loadMore,
 *     refresh
 *   } = useNotifications()
 *
 *   useEffect(() => {
 *     fetchNotifications()
 *   }, [fetchNotifications])
 *
 *   if (loading) return <LoadingSpinner />
 *   if (error) return <ErrorMessage error={error.message} />
 *
 *   // Show real-time connection status: realtime.connected
 *   // Display notifications with real-time updates from Pusher
 *   return <NotificationListComponent notifications={notifications} />
 * }
 * ```
 */
export function useNotifications() {
  const context = useNotificationContext()

  return {
    // State
    notifications: context.state.notifications,
    loading: context.state.loading.fetching,
    loadingMore: context.state.loading.loadingMore,
    error: context.state.error,
    unreadCount: context.state.unreadCount,
    hasInitiallyLoaded: context.state.hasInitiallyLoaded,

    // Real-time status
    realtime: {
      connected: context.state.realtime.connected,
      lastConnected: context.state.realtime.lastConnected,
      error: context.state.realtime.error,
      reconnectAttempts: context.state.realtime.reconnectAttempts,
    },

    // Pagination
    hasMore: context.state.pagination.hasMore,
    currentPage: context.state.pagination.currentPage,
    totalItems: context.state.pagination.totalItems,

    // Actions
    fetchNotifications: context.actions.fetchNotifications,
    loadMore: context.actions.loadMore,
    refresh: context.actions.refresh,
    clearError: context.actions.clearError,
    disconnect: context.actions.disconnect,

    // Utilities
    getNotificationById: context.utils.getNotificationById,
    getUnreadNotifications: context.utils.getUnreadNotifications,
    isExpired: context.utils.isExpired,
    formatDate: context.utils.formatDate,
  }
}

/**
 * Hook for accessing notification data with initial fetch
 *
 * This variant automatically fetches notifications on mount,
 * useful for components that need to display notifications immediately.
 *
 * @param params - Optional parameters for initial fetch
 * @example
 * ```typescript
 * function NotificationPage() {
 *   const { notifications, loading, error } = useNotificationsWithFetch()
 *
 *   if (loading) return <LoadingSpinner />
 *   if (error) return <ErrorMessage error={error.message} />
 *
 *   return <NotificationList notifications={notifications} />
 * }
 * ```
 */
export function useNotificationsWithFetch(params?: NotificationListParams) {
  const notificationHook = useNotifications()
  const { fetchNotifications } = notificationHook

  React.useEffect(() => {
    fetchNotifications(params)
  }, [fetchNotifications, params])

  return notificationHook
}

/**
 * Default export for convenience
 */
export default useNotifications
